import type { Component } from "solid-js";
import { Show } from "solid-js";
import posthog from "posthog-js";
import type { Form } from "../../../state/form.tsx";
import type { Folder } from "../../../types/Folder.ts";
import type CustomerId from "../../../state/CustomerId.ts";
import type { Item, SearchResultContentComponent } from "../types.ts";
import ApiService from "../../ApiService.ts";
import PermissionService from "../../PermissionService.ts";
import { POSTHOG_EVENTS } from "../../PostHogService.ts";
import { Icon } from "../../../components/icon/Icon";
import { IconTile } from "../../../components/icon/IconTile";
import Text from "../../../components/text/Text";
import { KbdHint } from "../../../components/kbd/KbdHint.tsx";
import { themeIconColour } from "../../../types";
import { useTranslation } from "../../../state/i18n.tsx";

type CreateFormSearchResultParams = {
  form: Form;
  folder: Folder;
  customerId: CustomerId;
};
export const createFormSearchResult = ({
  form,
  folder,
  customerId,
}: CreateFormSearchResultParams): Pick<Item, "id" | "actions"> & { ContentComponent: SearchResultContentComponent } => {
  const canEdit = () => PermissionService.canEditForm(customerId, form.id);

  return {
    id: form.id.toString(),
    ContentComponent: (props) => (
      <FormResultContent form={form} folder={folder} canEdit={canEdit()} highlighted={props.highlighted} />
    ),
    actions: {
      Enter: {
        type: "navigate",
        href: (customerId) => `/customers/${customerId}/folders/${folder.id}/forms/${form.id}`,
      },
      "Mod+Enter": {
        type: "navigate",
        href: async (customerId) => {
          if (!canEdit()) {
            return;
          }

          const formVersionId = await ApiService.resolveFormDraftId(form.id);
          posthog.capture(POSTHOG_EVENTS.GLOBAL_SEARCH_RESULT_SELECTED, { type: "EDIT_FORM" });
          return `/customers/${customerId}/forms/${form.id}/formVersions/${formVersionId}`;
        },
      },
    },
  };
};

type FormResultContentProps = {
  form: Form;
  folder: Folder;
  canEdit: boolean;
  highlighted: boolean;
};
const FormResultContent: Component<FormResultContentProps> = (props) => {
  const { t } = useTranslation();

  return (
    <div
      class="grid min-w-0 grid-cols-template gap-3 px-3 py-2.5"
      style={{
        "--column-template": "auto minmax(0, 1fr) auto",
      }}
    >
      <div class="pt-1">
        <IconTile icon={props.form.meta.icon} size="sm" color={themeIconColour[props.form.meta.theme.name]} border />
      </div>

      <div class="flex flex-col justify-center gap-0.5">
        <Text class="text-base-medium text-primary" as="p" text={props.form.meta.name} truncate />
        <Show when={props.form.meta.description}>
          {(description) => <Text class="text-small-regular text-secondary" as="p" text={description()} truncate />}
        </Show>
        <div class="flex items-center gap-1 text-small-regular text-secondary">
          <Icon name="folder" type="outline" size="sm" class="shrink-0 icon-tertiary" />
          <Text text={props.folder.meta.name} truncate />
        </div>
      </div>

      <Show when={props.canEdit && props.highlighted}>
        <div class="py-1.5">
          <KbdHint hotkeys="Mod+Enter" label={t(($) => $.GLOBAL_SEARCH_RESULTS_FORM_ACTIONS_EDIT)} />
        </div>
      </Show>
    </div>
  );
};
